import { cn } from '@parcel/ui';
import { AppLogo } from '@parcel/ui/app-logo';
import { Building2 } from 'lucide-react';
import { type ApiAccountDetail, useAccountDetail } from '../../lib/accounts.js';
import type { ApiContactDetail } from '../../lib/contacts.js';
import { monthYear, sourceFreshnessLine } from '../../lib/enrich.js';
import { normalizeExternalUrl } from '../../lib/external-url.js';
import { logoDomain, logoUrl } from '../../lib/logo.js';
import { formatRelativeDate } from '../../lib/mock-accounts.js';
import { PersonaTag } from '../enrich/persona-tag.js';
import { EmailCell, PhoneCell } from '../enrich/reach-cell.js';
import { Tip } from '../enrich/reveal-button.js';
import { CopyButton } from '../entity-drawer/copy-button.js';
import { PropertySection, StackedFieldGrid } from '../entity-drawer/entity-drawer-sections.js';
import { EntityAvatar } from '../entity-drawer/entity-drawer-shell.js';
import { ExpandableText } from '../entity-drawer/expandable-text.js';

const dash = <span className="text-muted-foreground">—</span>;

/** Each fact in its own cell: title, location and persona, then email and phone, then role start, source, updated. */
export function ContactDetailsSection({
  contact,
  columns,
}: {
  contact: ApiContactDetail;
  columns: 2 | 3;
}) {
  const sources = contact.reach?.sources;
  const since = sources?.role_started_on ?? contact.current_role?.started_on ?? null;
  const source = sources ? sourceFreshnessLine({ ...sources, role_started_on: null }) : null;
  return (
    <PropertySection title="Details">
      <StackedFieldGrid
        columns={columns}
        fields={[
          { label: 'Title', value: contact.title ?? dash },
          { label: 'Location', value: contact.location ?? dash },
          {
            label: 'Persona',
            value: contact.reach?.persona ? (
              <PersonaTag family={contact.reach.persona} match={contact.reach.persona_match} />
            ) : (
              dash
            ),
          },
          {
            label: 'Email',
            sensitive: true,
            value: <EmailCell contact={contact} variant="detail" />,
          },
          { label: 'Phone', sensitive: true, value: <PhoneCell contact={contact} /> },
          { label: 'In role since', value: since ? monthYear(since) : dash },
          { label: 'Source', value: source ?? dash },
          { label: 'Updated', value: formatRelativeDate(contact.updated_at) },
        ]}
      />
    </PropertySection>
  );
}

/** The person's own About text: two lines, then Read more, as a project's description reads. */
export function ContactAboutSection({ contact }: { contact: ApiContactDetail }) {
  if (!contact.about) return null;
  return (
    <section className="space-y-2 text-sm">
      <h3 className="font-semibold text-foreground">About</h3>
      <ExpandableText
        text={contact.about}
        collapsedLines={2}
        className="leading-6 text-foreground"
      />
    </section>
  );
}

function accountPlace(account: ApiAccountDetail): string | null {
  const place = [account.city, account.state].filter(Boolean).join(', ');
  return place || account.hq_address || null;
}

/** The firm the person works at: name, site and LinkedIn, its facts, and its ICP Score beside them. */
export function ContactAccountSection({
  contact,
  workspaceId,
  columns,
  onOpen,
}: {
  contact: ApiContactDetail;
  workspaceId: string | undefined;
  columns: 2 | 3;
  onOpen?: () => void;
}) {
  const account = useAccountDetail(contact.account_id, workspaceId).data?.account ?? null;
  const website = account?.website ?? account?.domains[0] ?? contact.account_website;
  const linkedin = normalizeExternalUrl(account?.linkedin_url ?? null);
  return (
    <PropertySection title="Account">
      <div className={cn('grid gap-5', columns === 3 && 'sm:grid-cols-2')}>
        <div className="min-w-0 space-y-4">
          <div className="flex min-w-0 items-center gap-3">
            <EntityAvatar
              src={logoUrl(website)}
              alt=""
              fallbackIcon={Building2}
              size="sm"
              href={normalizeExternalUrl(website)}
            />
            <div className="min-w-0 flex-1">
              <button
                type="button"
                onClick={onOpen}
                disabled={!onOpen}
                className="block max-w-full truncate text-left text-sm font-medium text-foreground hover:underline disabled:no-underline"
              >
                {account?.name ?? contact.account_name}
              </button>
              {website ? (
                <div className="flex min-w-0 items-center gap-1">
                  <a
                    href={normalizeExternalUrl(website) ?? undefined}
                    target="_blank"
                    rel="noreferrer"
                    className="truncate text-xs text-muted-foreground hover:underline"
                  >
                    {logoDomain(website)}
                  </a>
                  <CopyButton
                    value={logoDomain(website) ?? website}
                    label="Copy domain"
                    className="h-5 w-5"
                  />
                </div>
              ) : null}
            </div>
            {linkedin ? (
              <Tip tip="LinkedIn page">
                <a
                  href={linkedin}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`${account?.name ?? contact.account_name} on LinkedIn`}
                  className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border bg-background transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <AppLogo name="linkedin" size={16} title="LinkedIn" />
                </a>
              </Tip>
            ) : null}
          </div>
          <StackedFieldGrid
            columns={2}
            fields={[
              { label: 'Location', value: (account && accountPlace(account)) ?? dash },
              {
                label: 'Projects',
                value: account ? account.projects_count.toLocaleString('en-US') : dash,
              },
            ]}
          />
        </div>
      </div>
    </PropertySection>
  );
}
